import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

/**
 * SmoothScroll (React)
 *
 * Wrap your app once:
 *   <SmoothScrollProvider lerp={0.1}>
 *     <App />
 *   </SmoothScrollProvider>
 *
 * Then, anywhere inside:
 *   const { scrollTo } = useSmoothScroll();
 *   scrollTo("#section-2", { offset: -50 });
 *
 * Listen to scroll updates (optional):
 *   window.addEventListener("smoothScroll", (e) => e.detail.scroll)
 */

const SmoothScrollContext = createContext(null);

function detectMobile() {
  const hasTouch =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  const isUserAgentMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(navigator.userAgent.toLowerCase());
  return (hasTouch && isSmallScreen) || isUserAgentMobile;
}

const getMaxScroll = () =>
  Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

const isHorizontallyScrollable = (element) => {
  let el = element;
  while (el && el !== document.body) {
    const { overflowX } = window.getComputedStyle(el);
    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      el.scrollWidth > el.clientWidth
    ) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
};

const isTypingTarget = (el) =>
  el &&
  (el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable);

export function SmoothScrollProvider({
  children,
  lerp = 0.1, // 0-1, lower = smoother
  desktopOnly = true,
  disabled = false,
}) {
  // Mutable scroll state lives in a ref so it never triggers re-renders
  const state = useRef({ target: 0, current: 0, raf: null, active: false });

  useEffect(() => {

    if (disabled) return;

    const s = state.current;
    const root = document.documentElement;
    const prev = {
      rootBehavior: root.style.scrollBehavior,
      bodyOverflow: document.body.style.overflow,
    };

    // Native behaviour on mobile
    if (desktopOnly && detectMobile()) {
      root.style.scrollBehavior = "smooth";
      return () => {
        root.style.scrollBehavior = prev.rootBehavior;
      };
    }

    s.current = window.scrollY || window.pageYOffset;
    s.target = s.current;
    s.active = true;

    root.style.scrollBehavior = "auto";
    //document.body.style.overflow = "hidden";
    let touchY = 0;
    const clamp = () => {
      s.target = Math.max(0, Math.min(s.target, getMaxScroll()));
    };

    const animate = () => {
      const diff = s.target - s.current;

      if (Math.abs(diff) < 0.05) {
        // Settled: snap, apply once, and stop the loop until the next input
        s.current = s.target;
        apply();
        s.raf = null;
        return;
      }

      s.current += diff * lerp;
      apply();
      s.raf = requestAnimationFrame(animate);
    };

    const apply = () => {
      const y = Math.round(s.current * 100) / 100;
      window.scrollTo(0, y);
      window.dispatchEvent(
        new CustomEvent("smoothScroll", {
          detail: { scroll: y, target: s.target },
        })
      );
    };

    const kick = () => {
      if (s.raf === null) s.raf = requestAnimationFrame(animate);
    };

    const onWheel = (e) => {
      if (e.ctrlKey) return; // let pinch-zoom / ctrl+wheel zoom through
      if (isHorizontallyScrollable(e.target)) return;
      e.preventDefault();
      s.target += e.deltaY;
      clamp();
      kick();
    };

    const onTouchStart = (e) => {
      touchY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (isHorizontallyScrollable(e.target)) return;
      e.preventDefault();
      const y = e.touches[0].clientY;
      s.target += touchY - y;
      touchY = y;
      clamp();
      kick();
    };

    const onKeyDown = (e) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      const step = 40;
      const page = window.innerHeight - 40;

      switch (e.key) {
        case "ArrowUp":
          s.target -= step;
          break;
        case "ArrowDown":
          s.target += step;
          break;
        case "Home":
          s.target = 0;
          break;
        case "End":
          s.target = getMaxScroll();
          break;
        case "PageUp":
          s.target -= page;
          break;
        case "PageDown":
          s.target += page;
          break;
        default:
          return;
      }

      e.preventDefault();
      clamp();
      kick();
    };

    const onResize = () => {
      clamp();
      s.current = Math.min(s.current, s.target);
      kick();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("smoothScroll:kick", kick);

    return () => {
      window.removeEventListener("smoothScroll:kick", kick);
      s.active = false;
      if (s.raf !== null) cancelAnimationFrame(s.raf);
      s.raf = null;

      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);

      root.style.scrollBehavior = prev.rootBehavior;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, [lerp, desktopOnly, disabled]);

  // Stable API exposed through context
  const api = useMemo(
    () => ({
      scrollTo(target, { offset = 0 } = {}) {
        const currentState = state.current;

        let position;
        if (typeof target === "number") {
          position = target;
        } else {
          const el =
            typeof target === "string" ? document.querySelector(target) : target;
          if (!el) {
            console.warn("SmoothScroll: target element not found");
            return;
          }
          const base = currentState.active ? currentState.current : window.scrollY;
          position = el.getBoundingClientRect().top + base;
        }

        if (currentState.active) {
          currentState.target = Math.max(0, Math.min(position + offset, getMaxScroll()));
          if (currentState.raf === null) {
            // Restart the loop (defined inside the effect, so nudge via event)
            window.dispatchEvent(new Event("smoothScroll:kick"));
          }
        } else {
          // Smooth scrolling is off / mobile: fall back to native
          window.scrollTo({ top: position + offset, behavior: "smooth" });
        }
      },
    }),
    []
  );

  return (
    <SmoothScrollContext.Provider value={api}>
      {children}
    </SmoothScrollContext.Provider>
  );
}

export function useSmoothScroll() {
  const ctx = useContext(SmoothScrollContext);
  if (!ctx) {
    throw new Error("useSmoothScroll must be used inside <SmoothScrollProvider>");
  }
  return ctx;
}

export default SmoothScrollProvider;